import { useEffect, useState } from "react";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import dayjs from "dayjs";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid";

import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import { useSetInterval } from "../../hooks/useSetInterval";

import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";

import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import ProgressBar from "../../components/ProgressBar";
import TableRowNoResults from "../../components/TableRowNoResults";
import {
  StripedTableRow,
  StripedTableCell,
} from "../../components/StripedTable";
import PassDetailsDialog from "../../components/faculty/PassDetailsDialog";
import { getUserByBarcode, getUserEid } from "../../services/UserService";
import { getSchoolTeachers } from "../../services/SchoolService";

import {
  getPassLocations,
  calcTimeRemain,
  getOpenTeacherPasses,
  getOpenKioskPasses,
  getPassTypes,
  createPassByTeacher2,
  closeKioskUserPasses,
} from "../../services/PassService";
import clone from "lodash-es/clone";
import { doService } from "../../services/ServiceHelper";
import { showError, showToast } from "../../components/ATPDialogs";
import { secondsToMinutesAndSeconds } from "../../services/StringService";

const PassKiosk = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_openPasses, setOpenPasses] = useState([]);
  const [_dialogPassDetailsOpen, setDialogPassDetailsOpen] = useState(false);
  const [_selectedPass, setSelectedPass] = useState({});
  const [pageFocused, setPageFocused] = useState(document.hasFocus());
  const [_currentUser, setCurrentUser] = useState({});
  const [_dialogOpen, setDialogOpen] = useState(false);
  const [passTypeEid, setPassTypeEid] = useState("XX");
  const [passTypes, setPassTypes] = useState([]);
  const [toOption, setToOption] = useState("XX");
  const [teachers, setTeachers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [toLocationId, setToLocationId] = useState("XX");
  const [toTeacherEid, setToTeacherEid] = useState("XX");
  const [_studentId, setStudentId] = useState("");
  const [_defaultBathroomLocationId, setDefaultBathroomLocationId] =
    useState(null);

  let _capturedCode = "";

  const STATUS_CREATED = 0;
  const STATUS_APPROVED = 50;
  const STATUS_PAST_TIMEOUT = 700;

  useEffect(() => {
    initPassTypes();
    initLocations();
    initTeachers();
    initOpenPasses();

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        console.log("ENTER");
        // process and reset
        doExamineCode(_capturedCode);
        _capturedCode = "";
        return;
      }

      // ignore modifier keys, only handle real characters
      if (event.key.length === 1) {
        const code = _capturedCode;
        _capturedCode += event.key;
      }

      // handle Backspace
      if (event.key === "Backspace") {
        console.log("Backspace");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    const handleFocus = () => setPageFocused(true);
    const handleBlur = () => setPageFocused(false);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const isSomethingFocused = () => {
    return document.hasFocus();
  };

  const doExamineCode = (capturedCode) => {
    if (capturedCode.length === 0) {
      showError("Unable to Create Pass", "Code cannot be empty.");
      return;
    }

    if (capturedCode.length < 6) {
      showError(
        "Unable to Create Pass",
        "Code cannot be less than 6 characters.",
      );
      return;
    }

    setShowProgress(true);
    doService(getUserByBarcode(_school, _jwt, capturedCode), (result) => {
      setShowProgress(false);

      if (result.data == null) {
        showError("Unable to Create Pass", "Code does not exist for student.");
        return;
      }

      setStudentId("");
      const user = result.data;
      setCurrentUser(user);

      // see if student has open passes from this kiosk, if so, mark as returned
      doGetOpenKioskPasses(user);
    });

    // console.log("CODE: " + capturedCode);
  };

  const doGetOpenKioskPasses = (user) => {
    setShowProgress(true);
    doService(getOpenKioskPasses(_school, _jwt, user.eid), (result) => {
      setShowProgress(false);
      console.log(result.data);

      if (result.data.length > 0) {
        // we're closing existing pass(es) (jic)
        doCloseKioskPasses(user);
      } else {
        // we're creating a new pass
        doShowDialog(user);
      }
    });
  };

  const doShowDialog = (user) => {
    setPassTypeEid("XX");
    setToLocationId("XX");
    setToTeacherEid("XX");
    setDialogOpen(true);
  };

  const doCloseKioskPasses = (user) => {
    doService(closeKioskUserPasses(_school, _jwt, user.eid), (result) => {
      setShowProgress(false);
      showToast("S", "Pass Closed");
      initOpenPasses();
    });
  };

  // run every 15s
  useSetInterval(() => {
    initOpenPasses();
  }, 15000);

  useSetInterval(() => {
    const passes = clone(_openPasses);
    calcTimeRemain(passes);
    setOpenPasses(passes);
  }, 5000);

  const initOpenPasses = () => {
    setShowProgress(true);
    doService(getOpenTeacherPasses(_school, _jwt), (result) => {
      setShowProgress(false);
      calcTimeRemain(result.data.open_passes);
      setOpenPasses(result.data.open_passes);

      if (result.data.default_locations) {
        setDefaultBathroomLocationId(
          result.data.default_locations.bathroom_location_id || null,
        );
      }
    });
  };

  const initPassTypes = () => {
    setShowProgress(true);
    doService(getPassTypes(_school, _jwt), (result) => {
      setShowProgress(false);
      setPassTypes(result.data);
    });
  };

  const initLocations = () => {
    setShowProgress(true);
    doService(getPassLocations(_school, _jwt), (result) => {
      setShowProgress(false);
      setLocations(result.data);
    });
  };

  const initTeachers = () => {
    setShowProgress(true);
    doService(getSchoolTeachers(_school, _jwt), (result) => {
      setShowProgress(false);
      setTeachers(result.data);
    });
  };

  const handleClickPassDetailsClose = (doReload = false) => {
    setDialogPassDetailsOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const onClickCreate = () => {
    if (passTypeEid === "") {
      window.showAlert("Please select a Pass Type.");
      return;
    }

    if (isRequiresLocation()) {
      if (toOption === "T" && toTeacherEid === "XX") {
        alert("Please select a Teacher.");
        return;
      }
      if (toOption === "L" && toLocationId === "XX") {
        alert("Please select a Location.");
        return;
      }
    }

    savePass();
  };

  const savePass = () => {
    setShowProgress(true);
    let toLocId = toLocationId;
    let toTeaId = toTeacherEid;

    if (toOption === "L") {
      toTeaId = "";
    } else {
      toLocId = "";
    }

    if (toTeaId === "XX") toTeaId = "";
    if (toLocId === "XX") toLocId = "";

    const dDate = dayjs();
    createPassByTeacher2(
      _school,
      _jwt,
      _currentUser.eid,
      passTypeEid,
      dDate,
      getUserEid(_jwt),
      toLocId,
      toTeaId,
      "",
      "kiosk",
      "kiosk",
      "N",
    )
      .then((result) => {
        if (result.detail) {
          window.showAlert("Unable to Create Pass: " + result.detail);
          return;
        }

        if (result.detail) {
          window.showAlert("Unable to Create Pass: " + result.detail);
          return;
        }
        setDialogOpen(false);
        showToast(
          "S",
          "Pass Created!  Your pass has been created AND APPROVED.",
        );
        initOpenPasses();
      })
      .catch((error) => {
        showToast("E", "Unable to Create Pass.");
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const getAbbreviated = (name) => {
    return name.substring(0, 1) + ".";
  };

  const getPassType = (eid) => {
    for (let p of passTypes) {
      if (p.eid === eid) {
        return p;
      }
    }
    return null;
  };

  const getLocationIdByEid = (eid) => {
    for (let l of locations) {
      if (l.eid === eid) {
        return l.id;
      }
    }
    return null;
  };

  const handleChangePassType = (event) => {
    setPassTypeEid(event.target.value);
    // setSelectedPassType(obj.value);
    const passType = getPassType(event.target.value);
    if (passType != null) {
      if (passType.defaultLocationEid != null) {
        setToOption("L");
        setToLocationId(getLocationIdByEid(passType.defaultLocationEid));
      } else if (passType.isBathroom === "Y") {
        setToOption("L");
        // see if the teacher has a bathroom set for them
        // see if there is a default bathroom location ID
        if (_defaultBathroomLocationId != null) {
          setToLocationId(_defaultBathroomLocationId);
        }
      }
    }
  };

  const handleChangeToLocation = (event) => setToLocationId(event.target.value);
  const handleChangeToTeacher = (event) => setToTeacherEid(event.target.value);

  const getSelectedPassType = (passTypeEid) => {
    for (let i = 0; i < passTypes.length; i++) {
      if (passTypes[i].eid === passTypeEid) {
        return passTypes[i];
      }
    }
    return null;
  };

  const isRequiresLocation = () => {
    const passType = getSelectedPassType(passTypeEid);
    if (passType == null) {
      return false;
    }
    console.log(passType);

    // if pass type found (and not "")
    if (passType.requiresLocation === "Y") {
      return true;
    }

    return false;
  };

  const handleChangeToOption = (event) => {
    setToOption(event.target.value);
  };

  const getPassDestinationText = (pass) => {
    let toHtml = "";

    if (pass.toLocationNname != null) {
      toHtml = `${pass.toLocationName} (Location)`;
    } else if (pass.toTeacherLastName != null) {
      toHtml = `${pass.toTeacherNastName}, ${pass.toTeacherFirstName} (Teacher)`;
    } else {
      toHtml = "Bathroom";
    }
    return toHtml;
  };

  const onClickFindStudentId = () => {
    doExamineCode(_studentId);
  };

  return (
    <>
      <DrawerAppBar
        disableLogout={true}
        showLogout={false}
        showHamburger={false}
      />

      <Container sx={{ mt: 1 }}>
        <Title text="Hall Pass Kiosk" showBack={false} />
        <ProgressBar showProgress={showProgress} />
        {/* <Alert severity="info">
          This page allows you to use a barcode scanner to scan a student's ID,
          and also to manually enter the ID.
          <br />
          When using a scanner, this page needs to be clicked upon for it to
          work. The bar below under "Bar Code Scanner Status" will be green to
          indicate that you can scan the barcode. If it is red, then click
          anywhere on this page until it is green.
        </Alert> */}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
              <AccordionSummary className="accordionTitle">
                Enter your Student ID
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info">
                  This page allows you to use a barcode scanner to scan a
                  student's ID, and manually enter the ID.
                  <br />
                  The student should scan their barcode to start the hall pass
                  process. When they return, they should scan their barcode
                  again to mark the pass as returned.
                </Alert>

                <InputLabel sx={{ mt: 0 }}>Student ID:</InputLabel>
                <TextField
                  margin="dense"
                  type="text"
                  fullWidth
                  size="small"
                  onChange={(evt) => setStudentId(evt.target.value)}
                  placeholder="Required Field"
                  value={_studentId}
                  sx={{ mt: 0 }}
                />
                <Button variant="contained" onClick={onClickFindStudentId}>
                  Find Student ID to Create/Return Pass
                </Button>
              </AccordionDetails>
            </Accordion>
          </Grid>
          {/* <Grid item xs={6}>
            <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
              <AccordionSummary className="accordionTitle">
                Scan your Bar Code
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info">
                  The student should scan their barcode to start the hall pass
                  process. When they return, they should scan their barcode
                  again to mark the pass as returned.
                </Alert>

                <InputLabel sx={{ mt: 2 }}>Bar Code Scanner Status:</InputLabel>
                {pageFocused === true && (
                  <Alert severity="success">
                    You Can Scan Barcodes Now (page is focused)
                  </Alert>
                )}
                {pageFocused === false && (
                  <Alert severity="error">
                    Page needs to be Clicked to scan barcodes.
                  </Alert>
                )}

                {isSomethingFocused()}
              </AccordionDetails>
            </Accordion>
          </Grid> */}
        </Grid>

        <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
          <AccordionSummary className="accordionTitle">
            {"Open Passes (" + _openPasses.length + ")"}
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
              <Table aria-label="open passes" size="small">
                <TableHead>
                  <StripedTableRow>
                    <StripedTableCell>Student Name</StripedTableCell>
                    <StripedTableCell>Type</StripedTableCell>
                    <StripedTableCell>Destination</StripedTableCell>
                  </StripedTableRow>
                </TableHead>
                <TableBody>
                  {_openPasses.map((p) => (
                    <StripedTableRow key={p.eid}>
                      <StripedTableCell style={{ verticalAlign: "top" }}>
                        <b>
                          {p.student_last_name}, {p.student_first_name}
                        </b>
                        <br />
                        <div style={{ fontStyle: "italic" }}>
                          {p.status === STATUS_CREATED && (
                            <>Created/Requested</>
                          )}
                          {p.status === STATUS_APPROVED && <>Approved</>}{" "}
                          {p.status === STATUS_PAST_TIMEOUT && (
                            <>Past Timeout</>
                          )}
                          <br />
                          {p.timeRemainSeconds > 0 && (
                            <>
                              Remaining:{" "}
                              {secondsToMinutesAndSeconds(p.timeRemainSeconds)}
                            </>
                          )}
                        </div>{" "}
                      </StripedTableCell>
                      <StripedTableCell>{p.type_name}</StripedTableCell>
                      <StripedTableCell>
                        {getPassDestinationText(p)}
                      </StripedTableCell>
                    </StripedTableRow>
                  ))}

                  <TableRowNoResults data={_openPasses} />
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Container>

      <PassDetailsDialog
        jwt={_jwt}
        school={_school}
        open={_dialogPassDetailsOpen}
        pass={_selectedPass}
        onClose={handleClickPassDetailsClose}
        allowToEdit={false}
        locations={locations}
      />

      <Dialog
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 480,
          },
        }}
        onClose={handleDialogClose}
        open={_dialogOpen}
        fullScreen={true}
      >
        <DialogTitle>
          Create Pass for {_currentUser.first_name} {_currentUser.last_name} (
          {_currentUser.grade})
        </DialogTitle>

        <DialogContent>
          <InputLabel sx={{ mt: 1 }}>* Pass Type:</InputLabel>
          <Select
            id="passTypeEid"
            onChange={handleChangePassType}
            value={passTypeEid}
            fullWidth
          >
            <MenuItem value="XX">-- PASS TYPE --</MenuItem>
            {passTypes.map((passType) => (
              <MenuItem key={passType.eid} value={passType.eid}>
                {passType.name}
              </MenuItem>
            ))}
          </Select>

          {isRequiresLocation() === true && passTypeEid !== "XX" && (
            <>
              <InputLabel sx={{ mt: 2 }}>* Go To:</InputLabel>
              <Select
                id="toOption"
                onChange={handleChangeToOption}
                value={toOption}
                fullWidth
              >
                <MenuItem value="XX">-- SELECT --</MenuItem>
                <MenuItem value="L">Location</MenuItem>
                <MenuItem value="T">Teacher</MenuItem>
              </Select>

              {toOption === "L" && (
                <>
                  <InputLabel sx={{ mt: 2 }}>* Location:</InputLabel>
                  <Select
                    id="toLocationId"
                    onChange={handleChangeToLocation}
                    value={toLocationId}
                    fullWidth
                  >
                    <MenuItem value="XX" key="XX">
                      -- LOCATION --
                    </MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {toOption === "T" && (
                <>
                  <>
                    <InputLabel sx={{ mt: 2 }}>* Teacher:</InputLabel>
                    <Select
                      id="toTeacherEid"
                      onChange={handleChangeToTeacher}
                      value={toTeacherEid}
                      fullWidth
                    >
                      <MenuItem value="XX" key="XX">
                        -- TEACHER --
                      </MenuItem>
                      {teachers.map((teacher) => (
                        <MenuItem key={teacher.eid} value={teacher.eid}>
                          {teacher.last_name},{" "}
                          {getAbbreviated(teacher.first_name)}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleDialogClose}>
            Close
          </Button>
          <Button variant="contained" onClick={onClickCreate}>
            Create Pass
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PassKiosk;
